import { useState } from 'react';
import { FiThumbsDown, FiRefreshCw } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { contractApi } from '../../../services/contractApi';
import { formatCpfCnpj } from './ContractSignPage.helpers';

const DISPLAY_FIELDS = [
    { key: 'person_name', label: 'Nome/Razão Social' },
    { key: 'person_gov_tax_id', label: 'CPF/CNPJ' },
    { key: 'person_type', label: 'Tipo Pessoa' },
    { key: 'operation_type', label: 'Tipo Operação' },
    { key: 'operation_value', label: 'Valor da Operação' },
];

interface ExtractionStepProps {
    documentId: string;
    contractId: string;
    onComplete: () => void;
    onBack: () => void;
    setLoading: (v: boolean) => void;
    extractedData: Record<string, unknown> | null;
    setExtractedData: (d: Record<string, unknown> | null) => void;
    iterationNumber: number;
    setIterationNumber: (n: number) => void;
}

const ExtractionStep = ({ documentId, contractId, onComplete, onBack, setLoading, extractedData, setExtractedData, iterationNumber, setIterationNumber }: ExtractionStepProps) => {
    const [negativeFeedbacks, setNegativeFeedbacks] = useState<Set<string>>(new Set());
    const maxAttemptsReached = iterationNumber >= 3;

    const handleRetry = async () => {
        if (negativeFeedbacks.size === 0) return;
        setLoading(true);
        try {
            const feedbacks = Array.from(negativeFeedbacks).map(f => ({ field: f, extractedValue: String(extractedData?.[f] || '') }));
            const result = await contractApi.createExtraction(documentId, contractId, feedbacks);
            const extraction = await contractApi.getExtractionPageForm(result.extractionId);
            setExtractedData(extraction.extractedData);
            setIterationNumber(extraction.iterationNumber);
            setNegativeFeedbacks(new Set());
            toast.info(`Re-extração (tentativa ${extraction.iterationNumber}/3)`);
        } catch (e: any) {
            toast.error(e.message || 'Erro na re-extração');
        } finally {
            setLoading(false);
        }
    };

    const toggleFeedback = (field: string) => {
        setNegativeFeedbacks(prev => { const n = new Set(prev); n.has(field) ? n.delete(field) : n.add(field); return n; });
    };

    const canRetry = negativeFeedbacks.size > 0 && !maxAttemptsReached;

    if (!extractedData) return null;

    return (
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Dados Extraídos (Tentativa {iterationNumber}/3)</h2>
                {canRetry && (
                    <button onClick={handleRetry} className="flex items-center gap-1 px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600">
                        <FiRefreshCw /> Extrair Novamente
                    </button>
                )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Clique em <FiThumbsDown className="inline text-red-500" /> se algum dado estiver incorreto.
            </p>

            <div className="space-y-3">
                {DISPLAY_FIELDS.map(f => {
                    const value = extractedData[f.key];
                    const hasNeg = negativeFeedbacks.has(f.key);
                    return (
                        <div key={f.key} className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${hasNeg ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20' : 'border-gray-200 dark:border-slate-700'}`}>
                            <div className="flex-1">
                                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">{f.label}</label>
                                <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{f.key === 'person_gov_tax_id' ? formatCpfCnpj(String(value || '')) : String(value || '-')}</p>
                            </div>
                            <button onClick={() => toggleFeedback(f.key)} className={`p-2 rounded-lg transition-colors ${hasNeg ? 'bg-red-500 text-white' : 'bg-gray-100 dark:bg-slate-700 text-gray-400 hover:bg-red-100 hover:text-red-500'}`}>
                                <FiThumbsDown size={16} />
                            </button>
                        </div>
                    );
                })}

                {/* Signers */}
                <div className="mt-6">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Signatários Extraídos</h3>
                    <div className="space-y-2">
                        {(extractedData.signers as { name: string; gov_tax_id?: string; role: string }[] || []).map((s, i) => (
                            <div key={i} className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-slate-800 rounded-lg text-sm">
                                <span className="font-medium text-gray-900 dark:text-white">{s.name}</span>
                                <span className="text-gray-500 dark:text-gray-400">{formatCpfCnpj(s.gov_tax_id)}</span>
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 rounded text-xs">{s.role}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="flex gap-3 mt-6">
                <button onClick={onBack} className="px-4 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 text-gray-700 dark:text-gray-300">Voltar</button>
                <button onClick={onComplete} disabled={negativeFeedbacks.size > 0 && !maxAttemptsReached}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
                    {negativeFeedbacks.size === 0 ? 'Confirmar Dados' : maxAttemptsReached ? 'Prosseguir com Ressalvas' : 'Resolva os feedbacks'}
                </button>
            </div>
        </div>
    );
};

export default ExtractionStep;
